import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, Inject, Optional, Renderer2, HostListener, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { EntityManagementComponent as ParentEntityManagementComponent } from '@shared-lib/chart-components/entity-management/entity-management.component';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@shared-lib/shared-services/environment-config.token';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ResponsiveLayoutService } from '@shared-lib/shared-services/responsive-layout.service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { catchError, map, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-entity-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './entity-management.component.html',
  styleUrls: ['./entity-management.component.scss'],
})
export class EntityManagementComponent extends ParentEntityManagementComponent {
  private static readonly DEBUG_LOG_KEY = 'PALANTIR_CONFIG_TABLE_DEBUG_V3';
  private static readonly TABLE_REFRESH_EVENT = 'palantir-table-refresh-request';
  private static readonly LAST_CREATED_RECORD_STORAGE_PREFIX = 'palantir-last-created-record';
  private static readonly HEADER_ATTRIBUTE_BASE_PATH = 'assets/header-attribute';
  private static readonly COLUMN_SCHEMA_SUFFIX = '_columns';
  private static readonly ALWAYS_HIDDEN_FIELD_TOKENS = new Set(['cpm']);
  private static readonly HIDDEN_FIELD_TOKENS = new Set(['is valid', 'created by', 'created date', 'updated by', 'updated date', 'uuid', 'cpm']);
  private static readonly ENTITY_HIDDEN_FIELD_TOKENS = new Map<string, Set<string>>([
    ['cu_rer_pa_history', new Set(['region', 'regional pricing flag'])],
  ]);
  private static readonly RER_PA_HISTORY_COLUMN_ORDER = new Map([
    ['code', 0],
    ['pricing action', 1],
    ['region', 2],
    ['change reason', 3],
    ['old price', 4],
    ['suggested future price', 5],
    ['new price', 6],
    // ['cpm', 7],
    ['regional pricing flag', 7],
    ['new effective date', 8],
    ['notes', 9],
  ]);
  private readonly customGlobalFilterService: GlobalFilterService;
  private readonly customConfigService: ConfigService;
  private readonly route: ActivatedRoute;
  private readonly router: Router;
  private readonly environmentConfig: EnvironmentConfig | null;
  private readonly http: HttpClient;
  private readonly headerOrderCache = new Map<string, string[] | null>();
  private lastCreatedRecordSnapshot: any = null;
  private lastSavedRecordSnapshot: any = null;
  private tableRefreshEventListener?: EventListener;

  // Custom download menu state
  showDownloadMenu = false;
  downloadMenuTop = 0;
  downloadMenuRight = 0;
  currentDownloadAction: any = null;
  @ViewChild('downloadConfirmDialog') downloadConfirmDialog?: TemplateRef<any>;
  @ViewChild('deleteDialog') deleteDialogTpl?: TemplateRef<any>;
  private deleteDialogRef?: any;
  @ViewChild('entityTheadContainer') entityTheadContainer?: ElementRef<HTMLElement>;

  // View Transactions (history) dialog state
  @ViewChild('historyDialog') historyDialog?: TemplateRef<any>;
  historyAction: any = null;
  historyRecords: any[] = [];
  historyLoading = false;
  historyPage = 1;
  historyPageSize = 10;
  historySortKey: string | null = null;
  historySortDirection: 'asc' | 'desc' = 'asc';
  historyAnnotations: string[] = [];
  newHistoryAnnotation = '';

  /** Keeps the fixed column-header row in sync when the user scrolls horizontally. */
  syncHeaderScroll(event: Event): void {
    if (this.entityTheadContainer) {
      this.entityTheadContainer.nativeElement.scrollLeft = (event.target as HTMLElement).scrollLeft;
    }
  }

  toggleDownloadMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.showDownloadMenu) {
      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      this.downloadMenuTop = rect.bottom + 4;
      this.downloadMenuRight = window.innerWidth - rect.right;
    }
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const downloadContainer = this.elementRef?.nativeElement?.querySelector('.download-container');
    if (downloadContainer && downloadContainer.contains(target)) {
      return;
    }
    this.showDownloadMenu = false;
  }

  openDeleteConfirmation(): void {
    if (this.getSelectedRecordsForAction().length === 0) return;
    if (this.deleteDialogTpl && this.dialog) {
      this.deleteDialogRef = this.dialog.open(this.deleteDialogTpl, {
        width: '320px',
        panelClass: 'custom-download-dialog-panel'
      });
    }
  }

  closeDeleteDialog(confirmed: boolean): void {
    if (this.deleteDialogRef) {
      this.deleteDialogRef.close();
      this.deleteDialogRef = undefined;
    }
    if (confirmed) {
      this.deleteSelectedRecords();
    }
  }

  openDownloadConfirmation(action: any): void {
    this.showDownloadMenu = false;
    this.currentDownloadAction = action;
    if (this.downloadConfirmDialog && this.dialog) {
      this.dialogRef = this.dialog.open(this.downloadConfirmDialog, {
        width: '320px',
        panelClass: 'custom-download-dialog-panel'
      });
    }
  }

  closeDialog(save: boolean, action?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    if (save && action) {
      // In Custom TS, standard super.handleHeaderAction is called if we are not 'menu' or 'add'
      super.handleHeaderAction(action.actionId, action);
    }
    this.currentDownloadAction = null;
  }

  get historyColumns(): any[] {
    return Array.isArray(this.historyAction?.tableColumns) ? this.historyAction.tableColumns : [];
  }

  get historyDialogTitle(): string {
    const baseTitle = this.historyAction?.dialogTitle || 'View Transactions';
    const labelKeys: string[] = Array.isArray(this.historyAction?.recordLabelKeys)
      ? this.historyAction.recordLabelKeys
      : ['code', 'name'];
    const record = this.selectedRecord || {};
    const recordLabel = labelKeys
      .map((key) => record?.[key])
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      .join(' ');
    return recordLabel ? `${baseTitle} for ${recordLabel}` : baseTitle;
  }

  get historyEmptyMessage(): string {
    return this.historyAction?.emptyStateMessage || 'No data to display';
  }

  get pagedHistoryRecords(): any[] {
    const start = (this.historyPage - 1) * this.historyPageSize;
    return this.historyRecords.slice(start, start + this.historyPageSize);
  }

  openHistoryDialog(action: any): void {
    this.historyAction = action || {};
    this.historyRecords = [];
    this.historyPage = 1;
    this.historyPageSize = Number(action?.pageSize) > 0 ? Number(action.pageSize) : 10;
    this.historySortKey = null;
    this.historySortDirection = 'asc';
    this.historyAnnotations = [];
    this.newHistoryAnnotation = '';
    this.loadHistoryRecords(action);

    if (this.historyDialog && this.dialog) {
      this.dialogRef = this.dialog.open(this.historyDialog, {
        width: action?.dialogWidth || '78vw',
        maxWidth: action?.dialogMaxWidth || '90vw',
        panelClass: 'custom-download-dialog-panel',
      });
    }
  }

  closeHistoryDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
    this.historyAction = null;
  }

  sortHistoryBy(key: string): void {
    if (this.historySortKey === key) {
      this.historySortDirection = this.historySortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.historySortKey = key;
      this.historySortDirection = 'asc';
    }

    const direction = this.historySortDirection === 'asc' ? 1 : -1;
    this.historyRecords = [...this.historyRecords].sort((a, b) => {
      const left = a?.[key];
      const right = b?.[key];
      if (left === right) {
        return 0;
      }
      if (left === null || left === undefined) {
        return -direction;
      }
      if (right === null || right === undefined) {
        return direction;
      }
      return String(left).localeCompare(String(right), undefined, { numeric: true }) * direction;
    });
    this.historyPage = 1;
  }

  addHistoryAnnotation(): void {
    const annotation = (this.newHistoryAnnotation || '').trim();
    if (!annotation) {
      return;
    }
    this.historyAnnotations = [...this.historyAnnotations, annotation];
    this.newHistoryAnnotation = '';
  }

  private loadHistoryRecords(action: any): void {
    const apiUrl = action?.apiUrl;
    if (!apiUrl) {
      return;
    }

    this.historyLoading = true;
    const method = String(action?.method || 'GET').toUpperCase();
    const request$ = method === 'POST'
      ? this.http.post(apiUrl, this.buildHistoryPayload(action))
      : this.http.get(apiUrl);

    request$
      .pipe(catchError(() => of(null)))
      .subscribe((response: any) => {
        this.historyRecords = this.extractHistoryRows(response);
        this.historyLoading = false;
      });
  }

  private buildHistoryPayload(action: any): any {
    const payload = JSON.parse(JSON.stringify(action?.customPayload || {}));
    const record = this.selectedRecord || {};
    const replaceTokens = (value: any): any => {
      if (typeof value === 'string') {
        return value
          .replace(/\{\{\s*selectedRecordId\s*\}\}/g, String(record?.id ?? ''))
          .replace(/\{\{\s*entity\s*\}\}/g, String(this.currentFilters?.['entity'] ?? ''));
      }
      if (Array.isArray(value)) {
        return value.map(replaceTokens);
      }
      if (value && typeof value === 'object') {
        Object.keys(value).forEach((key) => {
          value[key] = replaceTokens(value[key]);
        });
        return value;
      }
      return value;
    };
    return replaceTokens(payload);
  }

  private extractHistoryRows(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }
    const candidates = [
      response?.responseList,
      response?.data,
      response?.result,
      response?.records,
      response?.rows,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }
    return [];
  }

  override isFilterActionApplied(action: any): boolean {
    return super.isFilterActionApplied(action);
  }

  override getAppliedFilterSummaryLines(): string[] {
    const lines: string[] = [];
    Object.entries(this.currentFilters || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        lines.push(`${key}: ${value}`);
      }
    });
    return lines.length > 0 ? lines : ['No filters applied'];
  }

  constructor(
    configService: ConfigService,
    globalFilterService: GlobalFilterService,
    renderer: Renderer2,
    dialog: MatDialog,
    responsiveLayoutService: ResponsiveLayoutService,
    cdr: ChangeDetectorRef,
    http: HttpClient,
    route: ActivatedRoute,
    router: Router,
    private elementRef: ElementRef,
    @Optional() @Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfig | null,
  ) {
    super(
      configService,
      globalFilterService,
      renderer,
      dialog,
      responsiveLayoutService,
      cdr,
    );
    this.customGlobalFilterService = globalFilterService;
    this.customConfigService = configService;
    this.http = http;
    this.route = route;
    this.router = router;
    this.environmentConfig = environmentConfig;
  }

  override ngOnInit(): void {
    this.applyInitialDropdownValuesFromQueryParams();
    super.ngOnInit();
    this.attachTableRefreshListener();
  }

  override ngOnDestroy(): void {
    this.detachTableRefreshListener();
    super.ngOnDestroy();
  }

  override fetchDropdownOptions(dropdown: any): any {
    return super.fetchDropdownOptions(dropdown).pipe(
      map((resolvedDropdown: any) => {
        if (dropdown?.key === 'entity') {
          const previousEntityValue = this.normalizeRedirectParamValue(this.currentFilters?.[dropdown.key]);
          const normalizedEntityKey = this.resolveSelectedEntityKey(dropdown);
          if (normalizedEntityKey) {
            this.currentFilters[dropdown.key] = normalizedEntityKey;
            dropdown.defaultValue = normalizedEntityKey;
            this.syncHeaderTitleFromDropdown(dropdown.key);
            this.applySelectedEntityToPayload(normalizedEntityKey);

            if (previousEntityValue && previousEntityValue !== normalizedEntityKey) {
              this.loadRecords().subscribe();
            }
          }
        }

        if (dropdown?.key === 'model') {
          const normalizedModelKey = this.resolveSelectedDropdownValue(dropdown, this.currentFilters?.[dropdown.key]);
          if (normalizedModelKey) {
            this.currentFilters[dropdown.key] = normalizedModelKey;
            dropdown.defaultValue = normalizedModelKey;
          }
        }

        return resolvedDropdown;
      }),
    );
  }

  override loadRecords(): any {
    this.applySelectedEntityToPayload();
    this.restoreLastCreatedRecordSnapshot();
    return super.loadRecords().pipe(
      switchMap((records: any[]) =>
        this.loadHeaderOrder(this.resolveSelectedEntityKey()).pipe(
          map((configuredOrder: string[] | null) => {
            this.filterHiddenColumns();
            this.normalizeColumnLabels();
            this.applyConfiguredColumnOrder(configuredOrder);
            this.applyKnownColumnOrderFallback();
            this.publishVisibleColumnsSchema();
            return records;
          }),
        ),
      ),
    );
  }

  getColumnDisplayLabel(column: any): string {
    if (!column || typeof column !== 'object') {
      return '';
    }

    const rawKey = String(column?.key || '').trim();
    const rawLabel = String(column?.label || '').trim();
    const sourceLabel = rawLabel || rawKey;

    if (!sourceLabel) {
      return '';
    }

    const shouldNormalizeLabel =
      !rawLabel ||
      rawLabel === rawKey ||
      rawLabel.includes('_') ||
      /^[a-z0-9_]+$/i.test(rawLabel) ||
      /^[a-z]+(?:[A-Z][a-z0-9]*)+$/i.test(rawLabel);

    return shouldNormalizeLabel
      ? this.formatColumnLabel(sourceLabel)
      : rawLabel;
  }

  override formatCellValue(value: any, column?: any): string {
    const decimalFormattedValue = this.formatDecimalCellValue(value);
    if (decimalFormattedValue !== null) {
      return decimalFormattedValue;
    }

    const formattedValue = super.formatCellValue(value, column);
    const rewrittenValue = this.rewriteAttributeLabelString(formattedValue);
    return this.stripCodeTimeZone(rewrittenValue, column);
  }

  private stripCodeTimeZone(value: string, column?: any): string {
    const columnKey = String(column?.key || '').trim().toLowerCase();
    if (columnKey !== 'code') {
      return value;
    }

    if (this.resolveSelectedEntityKey().toLowerCase() !== 'cu_rer_pa_history') {
      return value;
    }

    // Drop the time/timezone portion of any embedded ISO timestamp, keeping the date.
    return value.replace(/(\d{4}-\d{2}-\d{2})T[\d:]+(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g, '$1');
  }

  override getDropdownOptionLabel(dropdown: any, option: any): string {
    const formattedLabel = super.getDropdownOptionLabel(dropdown, option);
    if (this.shouldPreserveDropdownLabel(dropdown)) {
      return formattedLabel;
    }

    return this.rewriteAttributeLabelString(formattedLabel);
  }

  override handleHeaderAction(actionId: string, action?: any): void {
    if (actionId === 'history') {
      this.openHistoryDialog(action);
      return;
    }

    if (actionId === 'delete') {
      this.openDeleteConfirmation();
      return;
    }

    if (actionId !== 'add') {
      super.handleHeaderAction(actionId, action);
      return;
    }

    const newRecord: Record<string, any> = {};
    this.filterHiddenColumns();
    const columns = Array.isArray(this.data?.columns) ? this.data.columns : [];
    columns.forEach((column: any) => {
      if (column?.key && column.key !== 'id') {
        newRecord[column.key] = null;
      }
    });
    newRecord['id'] = null;
    newRecord['__isDraftNewRecord'] = true;

    this.debugLog('add_record_initialized', {
      columnKeys: columns.map((column: any) => column?.key).filter(Boolean),
      newRecord,
    });

    this.selectRecord(newRecord);
  }

  override isHeaderActionDisabled(action: any): boolean {
    if (action?.actionId === 'clear_filter') {
      return !this.hasDialogFilterApplied;
    }

    return super.isHeaderActionDisabled(action);
  }

  override onDropdownSelectionChange(dropdownKey: any, selectedValue: any): any {
    const dropdown =
      this.data?.header?.dropdowns?.find((item: any) => item.key === dropdownKey) ||
      { key: dropdownKey };
    const option = this.getSelectedDropdownOption(dropdown, selectedValue);
    const normalizedValue = this.resolveSelectedDropdownValue(
      dropdown,
      this.getDropdownOptionValue(dropdown, selectedValue),
    );
    const normalizedLabel = option
      ? this.getDropdownOptionLabel(dropdown, option)
      : this.getDropdownSelectedLabel(dropdown, selectedValue);

    this.debugLog('dropdown_selection_change', {
      dropdownKey,
      rawSelectedValue: selectedValue,
      normalizedValue,
      normalizedLabel,
      currentFiltersBeforeUpdate: this.currentFilters,
    });

    this.currentFilters[dropdownKey] = normalizedValue;
    this.syncHeaderTitleFromDropdown(dropdownKey);
    this.syncSelectedEntityLabel(dropdownKey);

    if (this.data?.widgetId) {
      const isQueryParam = dropdown?.isQueryParam === true || this.data?.method?.toLowerCase() === 'post';
      this.customGlobalFilterService.updateFilterSelection(
        dropdownKey,
        [normalizedValue],
        isQueryParam,
        this.data.widgetId,
      );
    }

    if (this.handleDropdownRedirect(dropdown, normalizedValue, normalizedLabel)) {
      return;
    }

    this.data.columns = undefined;
    this.loadRecords().subscribe();
  }

  private handleDropdownRedirect(dropdown: any, selectedValue: any, selectedLabel?: string): boolean {
    const redirectConfig =
      dropdown?.redirect ||
      dropdown?.redirectConfig ||
      dropdown?.externalRedirect;

    if (!redirectConfig || redirectConfig.enabled === false) {
      this.debugLog('redirect_skipped_no_config', {
        dropdownKey: dropdown?.key,
        selectedValue,
        selectedLabel,
      });
      return false;
    }

    const baseUrl = this.resolveRedirectBaseUrl(redirectConfig);
    if (!baseUrl) {
      this.debugLog('redirect_skipped_no_base_url', {
        dropdownKey: dropdown?.key,
        selectedValue,
        selectedLabel,
        redirectConfig,
      });
      return false;
    }

    const resolvedLabel = selectedLabel || this.getDropdownSelectedLabel(dropdown, selectedValue);
    const shouldRedirect = this.shouldRedirectSelectedValue(redirectConfig, selectedValue, resolvedLabel);
    this.debugLog('redirect_decision', {
      dropdownKey: dropdown?.key,
      selectedValue,
      resolvedLabel,
      shouldRedirect,
      redirectConfig,
    });

    if (!shouldRedirect) {
      return false;
    }

    const redirectUrl = this.buildDropdownRedirectUrl(
      baseUrl,
      redirectConfig,
      dropdown,
      selectedValue,
      resolvedLabel,
    );

    if (!redirectUrl) {
      this.debugLog('redirect_skipped_empty_url', {
        dropdownKey: dropdown?.key,
        selectedValue,
        resolvedLabel,
      });
      return false;
    }

    this.debugLog('redirecting', {
      dropdownKey: dropdown?.key,
      selectedValue,
      resolvedLabel,
      redirectUrl,
    });

    window.open(redirectUrl, String(redirectConfig?.target || '_self'));
    return true;
  }

  private shouldRedirectSelectedValue(
    redirectConfig: any,
    selectedValue: any,
    selectedLabel: string,
  ): boolean {
    const configuredEntities = Array.isArray(redirectConfig?.entities)
      ? redirectConfig.entities
      : [];

    if (!configuredEntities.length) {
      return false;
    }

    const matchAgainst = String(redirectConfig?.matchAgainst || 'either')
      .trim()
      .toLowerCase();
    const ignoreCase = redirectConfig?.ignoreCase !== false;
    const entityDropdown = this.getDropdownConfigByKey('entity');
    const matchCandidates = this.getRedirectMatchCandidates(
      entityDropdown,
      selectedValue,
      selectedLabel,
      ignoreCase,
    );
    const entityMatchMode = String(redirectConfig?.entityMatchMode || 'include')
      .trim()
      .toLowerCase();

    const hasMatch = configuredEntities.some((entity: any) => {
      const normalizedEntity = this.normalizeRedirectMatchValue(entity, ignoreCase);
      if (!normalizedEntity) {
        return false;
      }

      if (matchAgainst === 'value') {
        return matchCandidates.value.has(normalizedEntity);
      }

      if (matchAgainst === 'label') {
        return matchCandidates.label.has(normalizedEntity);
      }

      return (
        matchCandidates.value.has(normalizedEntity) ||
        matchCandidates.label.has(normalizedEntity)
      );
    });

    this.debugLog('redirect_match_evaluation', {
      selectedValue,
      selectedLabel,
      matchAgainst,
      entityMatchMode,
      configuredEntities,
      valueCandidates: Array.from(matchCandidates.value),
      labelCandidates: Array.from(matchCandidates.label),
      hasMatch,
    });

    return entityMatchMode === 'exclude' ? !hasMatch : hasMatch;
  }

  private buildDropdownRedirectUrl(
    baseUrl: string,
    redirectConfig: any,
    dropdown: any,
    selectedValue: any,
    selectedLabel: string,
  ): string {
    try {
      const redirectUrl = new URL(baseUrl, window.location.origin);
      const queryParamName = String(
        redirectConfig?.queryParamName ||
        redirectConfig?.paramName ||
        dropdown?.key ||
        'entity',
      ).trim();
      const queryParamSource = String(redirectConfig?.queryParamSource || 'value')
        .trim()
        .toLowerCase();
      const queryParamValue =
        queryParamSource === 'label'
          ? selectedLabel
          : this.normalizeRedirectParamValue(selectedValue);

      redirectUrl.searchParams.set(queryParamName, queryParamValue);
      this.appendRedirectContextParams(redirectUrl, dropdown, selectedValue, selectedLabel);

      const additionalQueryParams = redirectConfig?.additionalQueryParams;
      if (additionalQueryParams && typeof additionalQueryParams === 'object') {
        Object.entries(additionalQueryParams).forEach(([key, value]) => {
          if (!key) {
            return;
          }

          redirectUrl.searchParams.set(
            key,
            this.resolveRedirectTemplateValue(value, selectedValue, selectedLabel, dropdown?.key),
          );
        });
      }

      this.debugLog('redirect_url_built', {
        baseUrl,
        queryParamName,
        queryParamSource,
        selectedValue,
        selectedLabel,
        finalUrl: redirectUrl.toString(),
      });

      return redirectUrl.toString();
    } catch (error) {
      console.error('Invalid dropdown redirect URL configuration:', error);
      this.debugLog('redirect_url_build_error', {
        baseUrl,
        selectedValue,
        selectedLabel,
        error,
      });
      return '';
    }
  }

  private normalizeRedirectMatchValue(value: any, ignoreCase: boolean): string {
    const normalized = this.normalizeRedirectParamValue(value);
    return ignoreCase ? normalized.toLowerCase() : normalized;
  }

  private normalizeRedirectParamValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    return typeof value === 'string' ? value : String(value);
  }

  private resolveRedirectTemplateValue(
    value: any,
    selectedValue: any,
    selectedLabel: string,
    dropdownKey: string,
  ): string {
    const normalizedValue = this.normalizeRedirectParamValue(selectedValue);

    if (value === '$value') {
      return normalizedValue;
    }

    if (value === '$label') {
      return selectedLabel;
    }

    if (value === '$key') {
      return dropdownKey || '';
    }

    return this.normalizeRedirectParamValue(value);
  }

  private resolveRedirectBaseUrl(redirectConfig: any): string {
    const envKey = String(
      redirectConfig?.baseUrlEnvKey ||
      redirectConfig?.urlEnvKey ||
      '',
    ).trim();

    if (envKey && this.environmentConfig) {
      const envValue = (this.environmentConfig as Record<string, unknown>)[envKey];
      if (typeof envValue === 'string' && envValue.trim()) {
        this.debugLog('redirect_base_url_from_env', {
          envKey,
          resolvedBaseUrl: envValue.trim(),
        });
        return envValue.trim();
      }
    }

    const fallbackUrl = String(redirectConfig?.baseUrl || redirectConfig?.url || '').trim();
    this.debugLog('redirect_base_url_fallback', {
      envKey,
      resolvedBaseUrl: fallbackUrl,
    });
    return fallbackUrl;
  }

  private appendRedirectContextParams(
    redirectUrl: URL,
    dropdown: any,
    selectedValue: any,
    selectedLabel: string,
  ): void {
    const selectedEntityOption = this.getSelectedDropdownOption(dropdown, selectedValue);
    const currentModelDropdown = this.getDropdownConfigByKey('model');
    const currentModelValue = currentModelDropdown
      ? this.currentFilters?.[currentModelDropdown.key]
      : undefined;
    const selectedModelOption = currentModelDropdown
      ? this.getSelectedDropdownOption(currentModelDropdown, currentModelValue)
      : null;
    const modelLabel = currentModelDropdown
      ? this.getDropdownSelectedLabel(currentModelDropdown, currentModelValue)
      : '';

    redirectUrl.searchParams.set('entity', this.normalizeRedirectParamValue(selectedValue));

    const entityId = this.resolveOptionField(selectedEntityOption, ['id', 'entityId', 'entity_id']);
    if (entityId !== '') {
      redirectUrl.searchParams.set('entityId', entityId);
    }

    if (selectedLabel) {
      redirectUrl.searchParams.set('entityDisplayName', selectedLabel);
    }

    const modelValue = this.normalizeRedirectParamValue(currentModelValue);
    if (modelValue) {
      redirectUrl.searchParams.set('model', modelValue);
    }

    const modelId = this.resolveOptionField(selectedModelOption, ['id', 'modelId', 'model_id']);
    if (modelId !== '') {
      redirectUrl.searchParams.set('modelId', modelId);
    }

    if (modelLabel) {
      redirectUrl.searchParams.set('modelName', modelLabel);
    }

    this.debugLog('redirect_context_params', {
      selectedEntityOption,
      selectedValue,
      selectedLabel,
      currentModelValue,
      selectedModelOption,
      finalSearchParams: redirectUrl.search,
    });
  }

  private getDropdownConfigByKey(dropdownKey: string): any | null {
    const dropdowns = Array.isArray(this.data?.header?.dropdowns)
      ? this.data.header.dropdowns
      : [];

    return dropdowns.find((item: any) => item?.key === dropdownKey) || null;
  }

  private getSelectedDropdownOption(dropdown: any, selectedValue: any): any | null {
    const optionsList = Array.isArray(dropdown?.optionsList) ? dropdown.optionsList : [];
    const exactValueMatch = optionsList.find(
      (option: any) => this.getDropdownOptionValue(dropdown, option) === selectedValue,
    );

    if (exactValueMatch) {
      return exactValueMatch;
    }

    const normalizedSelectedValue = this.normalizeRedirectParamValue(selectedValue).toLowerCase();
    return (
      optionsList.find((option: any) => {
        const optionValue = this.normalizeRedirectParamValue(
          this.getDropdownOptionValue(dropdown, option),
        ).toLowerCase();
        const optionLabel = this.normalizeRedirectParamValue(
          this.getDropdownOptionLabel(dropdown, option),
        ).toLowerCase();

        return optionValue === normalizedSelectedValue || optionLabel === normalizedSelectedValue;
      }) || null
    );
  }

  private resolveOptionField(option: any, keys: string[]): string {
    if (!option || typeof option !== 'object') {
      return '';
    }

    for (const key of keys) {
      const value = option?.[key];
      if (value !== undefined && value !== null && value !== '') {
        return this.normalizeRedirectParamValue(value);
      }
    }

    return '';
  }

  private applyInitialDropdownValuesFromQueryParams(): void {
    const dropdowns = Array.isArray(this.data?.header?.dropdowns)
      ? this.data.header.dropdowns
      : [];

    if (!dropdowns.length) {
      return;
    }

    const seededFilters = this.customGlobalFilterService.getCurrentFilters?.() || {};
    const queryParams = {
      ...(seededFilters?.queryParams || {}),
      ...(this.route?.snapshot?.queryParams || {}),
    } as Record<string, any>;

    dropdowns.forEach((dropdown: any) => {
      const queryParamKey = String(
        dropdown?.queryParamName ||
        dropdown?.redirect?.queryParamName ||
        dropdown?.paramName ||
        dropdown?.key ||
        '',
      ).trim();

      if (!queryParamKey || queryParams[queryParamKey] === undefined || queryParams[queryParamKey] === null) {
        return;
      }

      const rawValue = Array.isArray(queryParams[queryParamKey])
        ? queryParams[queryParamKey][0]
        : queryParams[queryParamKey];
      const normalizedValue = typeof rawValue === 'string'
        ? decodeURIComponent(rawValue)
        : rawValue;

      dropdown.defaultValue = normalizedValue;
      this.currentFilters[dropdown.key] = normalizedValue;
      this.debugLog('initial_query_param_applied', {
        dropdownKey: dropdown?.key,
        queryParamKey,
        rawValue,
        normalizedValue,
      });
    });
  }

  private applySelectedEntityToPayload(entityKey = this.resolveSelectedEntityKey()): void {
    if (!entityKey || !this.data || typeof this.data !== 'object') {
      return;
    }

    if (this.data?.customPayload?.requestData && typeof this.data.customPayload.requestData === 'object') {
      this.data.customPayload.requestData.entity = entityKey;
    }

    const headerActions = Array.isArray(this.data?.header?.actions) ? this.data.header.actions : [];
    headerActions.forEach((action: any) => {
      if (action?.defaultApplyPayload?.requestData && typeof action.defaultApplyPayload.requestData === 'object') {
        action.defaultApplyPayload.requestData.entity = entityKey;
      }
    });

    this.debugLog('payload_entity_applied', {
      entityKey,
      customPayloadRequestData: this.data?.customPayload?.requestData,
      actionPayloads: headerActions.map((action: any) => ({
        actionId: action?.actionId,
        entity: action?.defaultApplyPayload?.requestData?.entity,
      })),
    });
  }

  private resolveSelectedEntityKey(dropdown = this.getDropdownConfigByKey('entity')): string {
    if (!dropdown) {
      return '';
    }

    return this.resolveSelectedDropdownValue(dropdown, this.currentFilters?.[dropdown.key]);
  }

  private resolveSelectedDropdownValue(dropdown: any, rawValue: any): string {
    const normalizedRawValue = this.normalizeRedirectParamValue(rawValue);
    if (!normalizedRawValue) {
      return '';
    }

    const selectedOption = this.getSelectedDropdownOption(dropdown, rawValue);
    if (selectedOption) {
      return this.normalizeRedirectParamValue(this.getDropdownOptionValue(dropdown, selectedOption));
    }

    const optionsList = Array.isArray(dropdown?.optionsList) ? dropdown.optionsList : [];
    const normalizedRawValueLower = normalizedRawValue.toLowerCase();
    const matchedByLabel = optionsList.find((option: any) => {
      const optionLabel = this.normalizeRedirectParamValue(this.getDropdownOptionLabel(dropdown, option));
      return optionLabel.toLowerCase() === normalizedRawValueLower;
    });

    if (matchedByLabel) {
      return this.normalizeRedirectParamValue(this.getDropdownOptionValue(dropdown, matchedByLabel));
    }

    return normalizedRawValue;
  }

  private getRedirectMatchCandidates(
    dropdown: any,
    selectedValue: any,
    selectedLabel: string,
    ignoreCase: boolean,
  ): { value: Set<string>; label: Set<string> } {
    const selectedOption = this.getSelectedDropdownOption(dropdown, selectedValue);
    const valueCandidates = new Set<string>();
    const labelCandidates = new Set<string>();

    const addCandidate = (target: Set<string>, value: any): void => {
      const normalized = this.normalizeRedirectMatchValue(value, ignoreCase);
      if (normalized) {
        target.add(normalized);
      }
    };

    addCandidate(valueCandidates, selectedValue);
    addCandidate(labelCandidates, selectedLabel);

    if (selectedOption && typeof selectedOption === 'object') {
      addCandidate(valueCandidates, this.getDropdownOptionValue(dropdown, selectedOption));
      addCandidate(valueCandidates, selectedOption?.name);
      addCandidate(valueCandidates, selectedOption?.key);
      addCandidate(valueCandidates, selectedOption?.entity);

      addCandidate(labelCandidates, this.getDropdownOptionLabel(dropdown, selectedOption));
      addCandidate(labelCandidates, selectedOption?.display_name);
      addCandidate(labelCandidates, selectedOption?.displayName);
      addCandidate(labelCandidates, selectedOption?.label);
    }

    return {
      value: valueCandidates,
      label: labelCandidates,
    };
  }

  private normalizeColumnLabels(): void {
    if (!Array.isArray(this.data?.columns)) {
      return;
    }

    let hasChanges = false;
    const originalLabels = this.data.columns.map((column: any) => ({
      key: column?.key,
      label: column?.label,
    }));

    this.data.columns = this.data.columns.map((column: any) => {
      if (!column || typeof column !== 'object') {
        return column;
      }

      const normalizedLabel = this.getColumnDisplayLabel(column);
      if (normalizedLabel && normalizedLabel !== column.label) {
        hasChanges = true;
        return {
          ...column,
          label: normalizedLabel,
        };
      }

      return column;
    });

    if (hasChanges) {
      this.debugLog('column_labels_normalized', {
        before: originalLabels,
        after: this.data.columns.map((column: any) => ({
          key: column?.key,
          label: column?.label,
        })),
      });
    }
  }

  private filterHiddenColumns(): void {
    if (!Array.isArray(this.data?.columns)) {
      return;
    }

    const entityHiddenTokens = this.resolveEntityHiddenFieldTokens();

    this.data.columns = this.data.columns.filter((column: any) => {
      const candidates = [
        column?.key,
        column?.label,
        this.getColumnDisplayLabel(column),
      ];

      if (candidates.some((candidate) => this.isHiddenFieldToken(candidate))) {
        return false;
      }

      if (
        entityHiddenTokens &&
        candidates.some((candidate) => entityHiddenTokens.has(this.normalizeOrderToken(candidate)))
      ) {
        return false;
      }

      return true;
    });
  }

  private resolveEntityHiddenFieldTokens(): Set<string> | null {
    const entityKey = this.resolveSelectedEntityKey();
    if (!entityKey) {
      return null;
    }

    return EntityManagementComponent.ENTITY_HIDDEN_FIELD_TOKENS.get(entityKey.toLowerCase()) ?? null;
  }

  protected override formatColumnLabel(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    return normalizedValue
      .replace(/_/g, ' ')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .replace(/\w\S*/g, (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase())
      .replace(/\bHp\b/g, 'HP');
  }

  private shouldPreserveDropdownLabel(dropdown: any): boolean {
    const normalizedKey = String(dropdown?.key || '')
      .trim()
      .toLowerCase();

    return normalizedKey === 'entity';
  }

  private rewriteAttributeLabelString(value: string): string {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) {
      return '';
    }

    const hyphenMatch = normalizedValue.match(/^([^{}]+?)\s+-\s+([^{}]+)$/);
    if (hyphenMatch) {
      const left = hyphenMatch[1]?.trim();
      const right = hyphenMatch[2]?.trim();
      if (left && right) {
        return `${left}{${right}}`;
      }
    }

    return normalizedValue;
  }

  private formatDecimalCellValue(value: any): string | null {
    if (typeof value === 'number') {
      return Number.isInteger(value) ? null : value.toFixed(2);
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue || !this.isPlainNumericString(trimmedValue)) {
      return null;
    }

    const parsedValue = Number(trimmedValue);
    if (!Number.isFinite(parsedValue) || Number.isInteger(parsedValue)) {
      return null;
    }

    return parsedValue.toFixed(2);
  }

  private isPlainNumericString(value: string): boolean {
    return /^-?\d+(?:\.\d+)?$/.test(value);
  }

  private loadHeaderOrder(entityKey: string): any {
    const normalizedEntityKey = String(entityKey || '').trim();
    if (!normalizedEntityKey) {
      return of(null);
    }

    if (this.headerOrderCache.has(normalizedEntityKey)) {
      return of(this.headerOrderCache.get(normalizedEntityKey) ?? null);
    }

    const primaryUrl = `${EntityManagementComponent.HEADER_ATTRIBUTE_BASE_PATH}/${normalizedEntityKey}.json`;
    const legacyUrl = `${EntityManagementComponent.HEADER_ATTRIBUTE_BASE_PATH}/${normalizedEntityKey}/order.json`;

    return this.http.get(primaryUrl).pipe(
      catchError(() => this.http.get(legacyUrl)),
      map((response: any) => {
        const configuredOrder = this.parseHeaderOrder(response);
        this.headerOrderCache.set(normalizedEntityKey, configuredOrder);
        return configuredOrder;
      }),
      catchError(() => {
        this.headerOrderCache.set(normalizedEntityKey, null);
        return of(null);
      }),
    );
  }

  private parseHeaderOrder(config: any): string[] | null {
    const configuredOrder = Array.isArray(config)
      ? config
      : config?.columnOrder ?? config?.fieldOrder ?? config?.order;

    if (!Array.isArray(configuredOrder)) {
      return null;
    }

    const normalizedOrder = configuredOrder
      .map((value: any) => String(value || '').trim())
      .filter(Boolean);

    return normalizedOrder.length ? normalizedOrder : null;
  }

  private applyConfiguredColumnOrder(configuredOrder: string[] | null): void {
    if (!configuredOrder?.length || !Array.isArray(this.data?.columns)) {
      return;
    }

    const orderIndex = new Map<string, number>();
    configuredOrder.forEach((columnKey, index) => {
      orderIndex.set(this.normalizeOrderToken(columnKey), index);
    });

    const existingColumns = [...this.data.columns];
    this.data.columns = existingColumns.sort((left: any, right: any) => {
      const leftIndex = this.resolveConfiguredOrderIndex(left, orderIndex);
      const rightIndex = this.resolveConfiguredOrderIndex(right, orderIndex);

      if (leftIndex !== undefined && rightIndex !== undefined) {
        return leftIndex - rightIndex;
      }

      if (leftIndex !== undefined) {
        return -1;
      }

      if (rightIndex !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  private applyKnownColumnOrderFallback(): void {
    if (!Array.isArray(this.data?.columns) || !this.data.columns.length) {
      return;
    }

    const normalizedLabels = new Set(
      this.data.columns.map((column: any) => this.normalizeOrderToken(column?.label || column?.key)),
    );
    const expectedLabels = Array.from(EntityManagementComponent.RER_PA_HISTORY_COLUMN_ORDER.keys());
    const hasAllExpectedLabels = expectedLabels.every((label) => normalizedLabels.has(label));

    if (!hasAllExpectedLabels) {
      return;
    }

    this.data.columns = [...this.data.columns].sort((left: any, right: any) => {
      const leftOrder = this.resolveKnownColumnOrderIndex(left);
      const rightOrder = this.resolveKnownColumnOrderIndex(right);

      if (leftOrder !== undefined && rightOrder !== undefined) {
        return leftOrder - rightOrder;
      }

      if (leftOrder !== undefined) {
        return -1;
      }

      if (rightOrder !== undefined) {
        return 1;
      }

      return 0;
    });
  }

  private resolveConfiguredOrderIndex(column: any, orderIndex: Map<string, number>): number | undefined {
    const candidates = [
      column?.key,
      column?.label,
      this.getColumnDisplayLabel(column),
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeOrderToken(candidate);
      if (!normalizedCandidate) {
        continue;
      }

      const matchedIndex = orderIndex.get(normalizedCandidate);
      if (matchedIndex !== undefined) {
        return matchedIndex;
      }
    }

    return undefined;
  }

  private resolveKnownColumnOrderIndex(column: any): number | undefined {
    const candidates = [
      column?.label,
      column?.key,
      this.getColumnDisplayLabel(column),
    ];

    for (const candidate of candidates) {
      const normalizedCandidate = this.normalizeOrderToken(candidate);
      if (!normalizedCandidate) {
        continue;
      }

      const matchedOrder = EntityManagementComponent.RER_PA_HISTORY_COLUMN_ORDER.get(normalizedCandidate);
      if (matchedOrder !== undefined) {
        return matchedOrder;
      }
    }

    return undefined;
  }

  private normalizeOrderToken(value: any): string {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  private isHiddenFieldToken(value: any): boolean {
    const normalizedValue = this.normalizeOrderToken(value);
    if (EntityManagementComponent.ALWAYS_HIDDEN_FIELD_TOKENS.has(normalizedValue)) {
      return true;
    }

    if (this.showSystemAttribute) {
      return false; // Show them if the toggle is checked
    }
    return EntityManagementComponent.HIDDEN_FIELD_TOKENS.has(normalizedValue);
  }

  private publishVisibleColumnsSchema(): void {
    if (!this.data?.widgetId || !Array.isArray(this.data?.columns)) {
      return;
    }

    const schema = this.data.columns
      .filter((column: any) => column?.key)
      .map((column: any) => ({
        key: String(column.key).trim(),
        label: this.getColumnDisplayLabel(column),
      }))
      .filter((column: any) => column.key);

    this.customGlobalFilterService.updateFilterSelection(
      `${this.data.widgetId}${EntityManagementComponent.COLUMN_SCHEMA_SUFFIX}`,
      schema,
      false,
      this.data.widgetId,
    );
  }

  private debugLog(event: string, payload: any): void {
    console.log(`[${EntityManagementComponent.DEBUG_LOG_KEY}] ${event}`, payload);
  }

  private attachTableRefreshListener(): void {
    this.tableRefreshEventListener = ((event: Event) => {
      const customEvent = event as CustomEvent<any>;
      const detail = customEvent?.detail;
      const widgetId = String(detail?.widgetId || '').trim();
      if (!widgetId || widgetId !== this.data?.widgetId) {
        return;
      }

      if (detail?.response) {
        this.debugLog('custom_refresh_response_received', {
          widgetId,
          response: detail.response,
          saveContext: detail?.saveContext,
        });
        this.applyRefreshResponse(detail.response, detail?.saveContext);
        return;
      }

      if (detail?.requestData && typeof detail.requestData === 'object') {
        (this as any).pendingDialogRequestData = detail.requestData;
      }

      this.debugLog('custom_refresh_event_received', {
        widgetId,
        requestData: detail?.requestData,
      });
      this.loadRecords().subscribe();
    }) as EventListener;

    window.addEventListener(
      EntityManagementComponent.TABLE_REFRESH_EVENT,
      this.tableRefreshEventListener,
    );
  }

  private detachTableRefreshListener(): void {
    if (!this.tableRefreshEventListener) {
      return;
    }

    window.removeEventListener(
      EntityManagementComponent.TABLE_REFRESH_EVENT,
      this.tableRefreshEventListener,
    );
    this.tableRefreshEventListener = undefined;
  }

  private applyRefreshResponse(
    response: any,
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    this.rememberLastSavedRecord(saveContext);
    this.rememberLastCreatedRecord(saveContext);
    const refreshedRecords = this.extractRecordsFromRefreshResponse(response);
    const selectedRecordId = this.selectedRecord?.id ?? null;
    const previousSelectedRecord = this.selectedRecord;

    this.records = refreshedRecords;
    this.ensureSavedNewRecordIsVisible(saveContext);
    if (this.hasActiveSort()) {
      this.applyConsistentRecordOrdering();
    }

    if (!this.data?.columns && this.records.length > 0) {
      const excludedColumns = new Set(
        (Array.isArray(this.data?.excludeColumns) ? this.data.excludeColumns : []).map((item: any) => String(item).trim()),
      );
      const firstRecord = this.records[0];
      this.data.columns = Object.keys(firstRecord)
        .filter((columnKey) => columnKey !== 'id' && !excludedColumns.has(columnKey))
        .map((columnKey) => ({
          key: columnKey,
          label: this.formatColumnLabel(columnKey),
          sortable: true,
        }));
    }

    this.loadHeaderOrder(this.resolveSelectedEntityKey()).subscribe((configuredOrder: string[] | null) => {
      this.filterHiddenColumns();
      this.normalizeColumnLabels();
      this.applyConfiguredColumnOrder(configuredOrder);
      this.applyKnownColumnOrderFallback();

      if (this.records.length > 0) {
        const preferredRecord =
          this.resolveRecordBySnapshot(saveContext?.recordSnapshot) ||
          this.resolveRecordBySnapshot(previousSelectedRecord) ||
          this.resolveRecordBySelectedId(selectedRecordId) ||
          this.resolveRecordBySnapshot(this.lastSavedRecordSnapshot) ||
          this.records[0];
        this.selectRecord(preferredRecord);
      } else {
        this.selectRecord(null);
      }

      this.loading = false;
      if (this.data?.widgetId) {
        this.customGlobalFilterService.updateFilterSelection('loading', [this.loading], false, this.data.widgetId);
      }
    });
  }

  private extractRecordsFromRefreshResponse(response: any): any[] {
    const key = this.data?.key || null;
    const result = key ? response?.[key] : response;
    const responseList = Array.isArray(response?.responseList) ? response.responseList : [];
    const firstResponseEntry = responseList[0];
    const firstResponseData = firstResponseEntry?.data;

    const candidates = [
      result,
      result?.data,
      response?.data,
      key ? firstResponseEntry?.[key] : null,
      Array.isArray(firstResponseData) ? firstResponseData[0]?.data : null,
      firstResponseData,
      Array.isArray(firstResponseData) ? firstResponseData[0] : null,
    ];

    for (const candidate of candidates) {
      if (this.isMeaningfulRecordArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private rememberLastCreatedRecord(
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    if (!saveContext?.isNewRecord || !saveContext?.recordSnapshot) {
      return;
    }

    this.lastCreatedRecordSnapshot = {
      ...saveContext.recordSnapshot,
    };
    this.persistLastCreatedRecordSnapshot();
  }

  private rememberLastSavedRecord(
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    if (!saveContext?.recordSnapshot) {
      return;
    }

    this.lastSavedRecordSnapshot = {
      ...saveContext.recordSnapshot,
    };
  }

  private resolveRecordBySnapshot(snapshot: any): any | null {
    if (!snapshot || !Array.isArray(this.records) || this.records.length === 0) {
      return null;
    }

    const matchedRecordIndex = this.findRecordIndexBySnapshot(this.records, snapshot);
    return matchedRecordIndex >= 0 ? this.records[matchedRecordIndex] : null;
  }

  private resolveRecordBySelectedId(selectedRecordId: any): any | null {
    if (selectedRecordId === null || selectedRecordId === undefined || !Array.isArray(this.records)) {
      return null;
    }

    return this.records.find((record: any) => record?.id === selectedRecordId) || null;
  }

  private persistLastCreatedRecordSnapshot(): void {
    const storageKey = this.getLastCreatedRecordStorageKey();
    if (!storageKey || !this.lastCreatedRecordSnapshot) {
      return;
    }

    try {
      window.sessionStorage.setItem(storageKey, JSON.stringify(this.lastCreatedRecordSnapshot));
    } catch {
      // Ignore storage failures and keep the in-memory snapshot.
    }
  }

  private restoreLastCreatedRecordSnapshot(): void {
    if (this.lastCreatedRecordSnapshot) {
      return;
    }

    const storageKey = this.getLastCreatedRecordStorageKey();
    if (!storageKey) {
      return;
    }

    try {
      const storedValue = window.sessionStorage.getItem(storageKey);
      if (!storedValue) {
        return;
      }

      const parsedSnapshot = JSON.parse(storedValue);
      if (parsedSnapshot && typeof parsedSnapshot === 'object') {
        this.lastCreatedRecordSnapshot = parsedSnapshot;
      }
    } catch {
      // Ignore malformed storage values.
    }
  }

  private getLastCreatedRecordStorageKey(): string {
    const widgetId = String(this.data?.widgetId || '').trim();
    if (!widgetId) {
      return '';
    }

    const entityKey = String(this.resolveSelectedEntityKey() || this.currentFilters?.entity || 'default').trim();
    return `${EntityManagementComponent.LAST_CREATED_RECORD_STORAGE_PREFIX}:${widgetId}:${entityKey || 'default'}`;
  }

  private ensureSavedNewRecordIsVisible(
    saveContext?: { isNewRecord?: boolean; recordSnapshot?: any },
  ): void {
    if (!saveContext?.isNewRecord || !saveContext?.recordSnapshot || !Array.isArray(this.records)) {
      return;
    }

    const matchedRecordIndex = this.findRecordIndexBySnapshot(this.records, saveContext.recordSnapshot);
    if (matchedRecordIndex >= 0) {
      return;
    }

    this.records = [
      {
        ...saveContext.recordSnapshot,
      },
      ...this.records,
    ];
  }

  private hasActiveSort(): boolean {
    return !!this.sortColumn;
  }

  private applyConsistentRecordOrdering(): void {
    if (!Array.isArray(this.records) || this.records.length < 2) {
      return;
    }

    const sortColumn = this.hasActiveSort()
      ? (this.sortColumn as string)
      : null;
    if (!sortColumn) {
      return;
    }

    const direction = this.hasActiveSort() && this.sortDirection === 'desc' ? -1 : 1;
    const column = this.data?.columns?.find((item: any) => item?.key === sortColumn);

    this.records = [...this.records].sort((left: any, right: any) =>
      this.compareCustomRecordValues(left?.[sortColumn], right?.[sortColumn], column) * direction,
    );
  }

  private compareCustomRecordValues(leftValue: any, rightValue: any, column?: any): number {
    const normalizedLeft = this.getCustomSortableCellValue(leftValue, column);
    const normalizedRight = this.getCustomSortableCellValue(rightValue, column);

    if (normalizedLeft === normalizedRight) {
      return 0;
    }

    if (normalizedLeft === '') {
      return -1;
    }

    if (normalizedRight === '') {
      return 1;
    }

    const numericLeft = Number(normalizedLeft);
    const numericRight = Number(normalizedRight);
    const bothNumeric =
      normalizedLeft.trim() !== '' &&
      normalizedRight.trim() !== '' &&
      !Number.isNaN(numericLeft) &&
      !Number.isNaN(numericRight);

    if (bothNumeric) {
      return numericLeft - numericRight;
    }

    const dateLeft = Date.parse(normalizedLeft);
    const dateRight = Date.parse(normalizedRight);
    const bothDates = !Number.isNaN(dateLeft) && !Number.isNaN(dateRight);
    if (bothDates) {
      return dateLeft - dateRight;
    }

    return normalizedLeft.localeCompare(normalizedRight, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  private getCustomSortableCellValue(value: any, column?: any): string {
    return this.formatCellValue(value, column).trim();
  }

  private findRecordIndexBySnapshot(records: any[], snapshot: any): number {
    if (!Array.isArray(records) || !snapshot || typeof snapshot !== 'object') {
      return -1;
    }

    const snapshotCandidates = this.getSaveIdentityCandidates(snapshot);
    if (snapshotCandidates.length > 0) {
      const matchedIndex = records.findIndex((record: any) => {
        return this.recordsMatchSnapshot(record, snapshot, snapshotCandidates);
      });
      if (matchedIndex >= 0) {
        return matchedIndex;
      }
    }

    return this.findRecordIndexByFieldSimilarity(records, snapshot);
  }

  private recordsMatchSnapshot(record: any, snapshot: any, snapshotCandidates?: string[]): boolean {
    const recordCandidates = this.getSaveIdentityCandidates(record);
    const comparisonCandidates = snapshotCandidates || this.getSaveIdentityCandidates(snapshot);
    return comparisonCandidates.some((candidate) => recordCandidates.includes(candidate));
  }

  private getSaveIdentityCandidates(record: any): string[] {
    if (!record || typeof record !== 'object') {
      return [];
    }

    return [
      record?.id,
      record?.uuid,
      record?.UUID,
      record?.code,
      record?.Code,
    ]
      .filter((value) => value !== null && value !== undefined && String(value).trim() !== '')
      .map((value) => String(value).trim());
  }

  private findRecordIndexByFieldSimilarity(records: any[], snapshot: any): number {
    const comparableEntries = Object.entries(snapshot || {}).filter(([key, value]) =>
      key !== '__isDraftNewRecord' &&
      value !== null &&
      value !== undefined &&
      String(value).trim() !== '' &&
      ['string', 'number', 'boolean'].includes(typeof value),
    );

    if (!comparableEntries.length) {
      return -1;
    }

    const minimumMatchCount = Math.min(comparableEntries.length, 2);
    let bestMatchIndex = -1;
    let bestMatchScore = 0;

    records.forEach((record: any, index: number) => {
      if (!record || typeof record !== 'object') {
        return;
      }

      const matchScore = comparableEntries.reduce((score, [key, value]) => {
        return this.normalizeComparableValue(record[key]) === this.normalizeComparableValue(value)
          ? score + 1
          : score;
      }, 0);

      if (matchScore > bestMatchScore) {
        bestMatchScore = matchScore;
        bestMatchIndex = index;
      }
    });

    return bestMatchScore >= minimumMatchCount ? bestMatchIndex : -1;
  }

  private normalizeComparableValue(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private isMeaningfulRecordArray(candidate: any): candidate is any[] {
    if (!Array.isArray(candidate)) {
      return false;
    }

    if (candidate.length === 0) {
      return true;
    }

    return candidate.some((item: any) => {
      if (item === null || item === undefined) {
        return false;
      }

      if (typeof item !== 'object') {
        return true;
      }

      return Object.keys(item).length > 0;
    });
  }

  isPricingActionHistoryColumn(column: any): boolean {
    if (!column || typeof column !== 'object') {
      return false;
    }

    const normalizedKey = this.normalizeOrderToken(column?.key || '');
    const normalizedLabel = this.normalizeOrderToken(column?.label || this.getColumnDisplayLabel(column));

    return normalizedKey === 'pricing action history' ||
           normalizedLabel === 'pricing action history' ||
           normalizedKey === 'pricing history' ||
           normalizedLabel === 'pricing history';
  }

  onPricingActionIconClick(event: MouseEvent, record: any): void {
    event.stopPropagation();

    // Route to the Pricing Action History report, prefilling its Technical /
    // Marketing / Fuel Rating dropdowns from the clicked row. The report reads
    // these query params to load the history for the selected configuration.
    const queryParams: Record<string, string> = {};
    const carry = (paramKey: string) => {
      const value = this.resolvePricingConfigValue(record, paramKey);
      if (value) {
        queryParams[paramKey] = value;
      }
    };
    carry('technical_configuration');
    carry('marketing_configuration');
    carry('fuel_rating_hp');

    this.debugLog('pricing_action_report_navigate', { record, queryParams });
    this.router.navigate(['/reports/pricing-action-history'], { queryParams });
  }

  /**
   * Reads a configuration value off the clicked row. The record keys come from
   * the API and may not match the target token exactly (case / underscores /
   * spaces), so we fall back to a normalized key lookup, then reduce the value
   * to a plain string (unwrapping `{ code }` objects and `code{label}` strings).
   */
  private resolvePricingConfigValue(record: any, targetKey: string): string | null {
    if (!record || typeof record !== 'object') {
      return null;
    }

    if (record[targetKey] != null) {
      return this.stringifyPricingConfigValue(record[targetKey]);
    }

    const normalizedTarget = this.normalizeConfigToken(targetKey);
    for (const [key, value] of Object.entries(record)) {
      if (this.normalizeConfigToken(key) === normalizedTarget) {
        return this.stringifyPricingConfigValue(value);
      }
    }
    return null;
  }

  private normalizeConfigToken(value: string): string {
    return String(value).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private stringifyPricingConfigValue(value: any): string | null {
    if (value == null) {
      return null;
    }

    if (typeof value === 'object') {
      const candidate = value.code ?? value.value ?? value.id ?? value.name ?? value.label;
      return candidate != null ? this.stringifyPricingConfigValue(candidate) : null;
    }

    let str = String(value).trim();
    const braceIndex = str.indexOf('{');
    if (braceIndex > 0) {
      str = str.slice(0, braceIndex).trim();
    }
    return str || null;
  }
}
